<!-- Hero Section -->
<section class="relative bg-gradient-to-br from-gray-900 via-purple-900 to-violet-900 text-white overflow-hidden">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 left-0 w-1/3 h-1/3 bg-purple-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 right-0 w-1/3 h-1/3 bg-violet-500 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 py-24 md:py-32 relative z-10">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div class="text-center lg:text-left">
        <div class="inline-block bg-purple-500/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
          <span class="text-sm font-medium text-purple-300">
            <i class="bx bx-image mr-2"></i>Saved Images
          </span>
        </div>
        
        <h1 class="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
          Preconfigured Server <span class="text-purple-400">Images</span>
        </h1>
        
        <p class="text-xl text-gray-300 mb-8 max-w-2xl">
          Access our library of preconfigured server images with IIS, applications, and operating systems ready for instant deployment in your cloud environment.
        </p>
        
        <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-10">
          <a routerLink="/contact" class="bg-gradient-to-r from-purple-600 to-violet-700 hover:from-purple-700 hover:to-violet-800 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl inline-flex items-center justify-center">
            <i class="bx bx-image mr-2"></i>
            Browse Images
          </a>
          <a routerLink="/contact" class="bg-transparent border-2 border-purple-500 text-purple-300 hover:bg-purple-500/10 font-semibold py-3 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center">
            <i class="bx bx-phone mr-2"></i>
            Contact Sales
          </a>
        </div>
        
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-2xl">
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">100+</div>
            <div class="text-sm text-gray-400">Images</div>
          </div>
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">Instant</div>
            <div class="text-sm text-gray-400">Deployment</div>
          </div>
          <div class="bg-purple-500/10 backdrop-blur-sm rounded-xl p-4 border border-purple-500/30">
            <div class="text-3xl font-bold text-purple-400 mb-1">Updated</div>
            <div class="text-sm text-gray-400">Weekly</div>
          </div>
        </div>
      </div>
      
      <div class="relative">
        <div class="relative z-10 rounded-2xl overflow-hidden shadow-2xl border border-gray-700">
          <img src="assets/img/saved-images-hero.png" alt="Saved Images" class="w-full h-auto">
        </div>
        
        <div class="absolute -top-6 -left-6 bg-gradient-to-br from-purple-600 to-violet-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-windows text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Windows</div>
              <div class="text-xs">Server</div>
            </div>
          </div>
        </div>
        
        <div class="absolute -bottom-6 -right-6 bg-gradient-to-br from-violet-600 to-purple-700 rounded-xl p-5 shadow-xl w-44">
          <div class="flex items-center">
            <i class="bx bx-tux text-3xl mr-3"></i>
            <div>
              <div class="text-2xl font-bold">Linux</div>
              <div class="text-xs">Distributions</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- Image Categories Section -->
<section class="py-20 bg-gray-50">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-purple-100 text-purple-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Image Library
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Preconfigured <span class="text-purple-600">Server Images</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Ready-to-deploy server images with popular applications, optimized configurations, and security hardening.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
      <!-- Windows Server -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-blue-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-windows text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Windows Server</h3>
        <p class="text-gray-600 mb-4">
          Preconfigured Windows Server images with IIS, .NET Framework, and common enterprise applications.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Windows Server 2019/2022</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>IIS 10 pre-installed</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>.NET Framework & Core</span>
          </li>
        </ul>
      </div>

      <!-- Linux Distributions -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-green-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-tux text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Linux Distributions</h3>
        <p class="text-gray-600 mb-4">
          Popular Linux distributions with preinstalled web servers, LAMP/LEMP stacks, and development tools.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Ubuntu, CentOS, Debian</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Apache/NGINX included</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>PHP, Python, Node.js ready</span>
          </li>
        </ul>
      </div>

      <!-- Database Images -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-indigo-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-database text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Database Images</h3>
        <p class="text-gray-600 mb-4">
          Database server images with preconfigured settings, performance tuning, and optimized configurations.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>SQL Server, MySQL, PostgreSQL</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Optimized configurations</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>High availability setup</span>
          </li>
        </ul>
      </div>

      <!-- Security Hardened -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-red-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-red-500 to-rose-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-shield-quarter text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Security Hardened</h3>
        <p class="text-gray-600 mb-4">
          Security-hardened images following industry best practices and compliance requirements.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>CIS benchmark compliance</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Firewall pre-configured</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Security patches applied</span>
          </li>
        </ul>
      </div>

      <!-- Optimized Performance -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-amber-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-rocket text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Optimized Performance</h3>
        <p class="text-gray-600 mb-4">
          Performance-tuned images optimized for specific workloads and high-traffic applications.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Kernel optimization</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Resource tuning</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Caching configured</span>
          </li>
        </ul>
      </div>

      <!-- Regular Updates -->
      <div class="bg-white rounded-xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 border-l-4 border-teal-500 transform hover:-translate-y-2">
        <div class="w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center mb-6">
          <i class="bx bx-sync text-3xl text-white"></i>
        </div>
        <h3 class="text-xl font-bold text-gray-900 mb-3">Regular Updates</h3>
        <p class="text-gray-600 mb-4">
          Regularly updated images with the latest security patches, bug fixes, and application versions.
        </p>
        <ul class="space-y-2">
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Weekly security updates</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Latest application versions</span>
          </li>
          <li class="flex items-center text-gray-700 text-sm">
            <i class="bx bx-check-circle text-purple-600 mr-2"></i>
            <span>Automated patch management</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- Benefits Section -->
<section class="py-20 bg-white">
  <div class="container mx-auto px-4">
    <div class="text-center mb-16">
      <div class="inline-block bg-violet-100 text-violet-800 px-4 py-1 rounded-full text-sm font-medium mb-4">
        Key Benefits
      </div>
      <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
        Why Use <span class="text-violet-600">Saved Images</span>
      </h2>
      <p class="text-xl text-gray-600 max-w-3xl mx-auto">
        Deploy faster with preconfigured images that save time and ensure consistency across your infrastructure.
      </p>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
      <!-- Instant Deployment -->
      <div class="bg-gradient-to-br from-purple-50 to-violet-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
        <div class="w-16 h-16 bg-gradient-to-br from-purple-600 to-violet-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="bx bx-rocket text-3xl text-white"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Instant Deployment</h3>
        <p class="text-sm text-gray-600">
          Deploy servers in minutes with preconfigured images ready for production use.
        </p>
      </div>

      <!-- Consistency -->
      <div class="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
        <div class="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="bx bx-check-double text-3xl text-white"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Consistency</h3>
        <p class="text-sm text-gray-600">
          Ensure consistent configurations across all your server deployments.
        </p>
      </div>

      <!-- Time Savings -->
      <div class="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
        <div class="w-16 h-16 bg-gradient-to-br from-green-600 to-emerald-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="bx bx-time text-3xl text-white"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Time Savings</h3>
        <p class="text-sm text-gray-600">
          Save hours of manual configuration with ready-to-use server images.
        </p>
      </div>

      <!-- Best Practices -->
      <div class="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300">
        <div class="w-16 h-16 bg-gradient-to-br from-amber-600 to-orange-700 rounded-full flex items-center justify-center mx-auto mb-4">
          <i class="bx bx-shield-check text-3xl text-white"></i>
        </div>
        <h3 class="text-lg font-bold text-gray-900 mb-2">Best Practices</h3>
        <p class="text-sm text-gray-600">
          Images configured following industry best practices and security standards.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="py-20 bg-slate-900 text-white">
  <div class="container mx-auto px-4">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
      <div>
        <div class="inline-block bg-slate-800/70 text-purple-300 px-4 py-1 rounded-full text-sm font-medium mb-4">
          Image catalog and technical details
        </div>
        <h2 class="text-3xl md:text-4xl font-bold mb-4">
          Curated images for real-world
          <span class="text-purple-400">workloads</span>
        </h2>
        <p class="text-gray-200/90 mb-8 max-w-xl">
          Choose from a catalog of production-ready images covering Microsoft,
          Linux and application stacks. Every image is versioned, hardened and
          validated for predictable behavior in your environment.
        </p>

        <div class="overflow-x-auto bg-slate-800/70 border border-slate-700/70 rounded-2xl shadow-xl">
          <table class="min-w-full text-left text-sm">
            <thead class="bg-slate-900/70 text-gray-300 text-xs uppercase tracking-wide">
              <tr>
                <th class="px-5 py-3 font-semibold">Image</th>
                <th class="px-5 py-3 font-semibold">OS</th>
                <th class="px-5 py-3 font-semibold">Stack</th>
                <th class="px-5 py-3 font-semibold">Use case</th>
                <th class="px-5 py-3 font-semibold text-right">Deploy time</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-700/70">
              <tr class="hover:bg-slate-900/60 transition-colors">
                <td class="px-5 py-4">
                  <div class="font-semibold">IIS Web Application</div>
                  <div class="text-xs text-gray-400">Windows Server 2022 + IIS 10</div>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">Windows</td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-500/20 text-blue-200 text-xs font-medium">
                    .NET / IIS
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">
                  Enterprise web apps, portals, APIs
                </td>
                <td class="px-5 py-4 text-right text-sm text-gray-200">
                  <span class="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    ~5 minutes
                  </span>
                </td>
              </tr>
              <tr class="hover:bg-slate-900/60 transition-colors">
                <td class="px-5 py-4">
                  <div class="font-semibold">Linux NGINX Web</div>
                  <div class="text-xs text-gray-400">Ubuntu LTS + NGINX</div>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">Linux</td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-200 text-xs font-medium">
                    LEMP
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">
                  High-performance websites and APIs
                </td>
                <td class="px-5 py-4 text-right text-sm text-gray-200">
                  <span class="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    ~3 minutes
                  </span>
                </td>
              </tr>
              <tr class="hover:bg-slate-900/60 transition-colors">
                <td class="px-5 py-4">
                  <div class="font-semibold">SQL Server Standard</div>
                  <div class="text-xs text-gray-400">Windows Server + SQL Server</div>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">Windows</td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-200 text-xs font-medium">
                    Database
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">
                  Line-of-business and reporting workloads
                </td>
                <td class="px-5 py-4 text-right text-sm text-gray-200">
                  <span class="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    ~7 minutes
                  </span>
                </td>
              </tr>
              <tr class="hover:bg-slate-900/60 transition-colors">
                <td class="px-5 py-4">
                  <div class="font-semibold">WordPress Optimized</div>
                  <div class="text-xs text-gray-400">Linux + NGINX + PHP-FPM</div>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">Linux</td>
                <td class="px-5 py-4">
                  <span class="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-200 text-xs font-medium">
                    CMS
                  </span>
                </td>
                <td class="px-5 py-4 text-gray-200 text-sm">
                  Content-heavy websites and blogs
                </td>
                <td class="px-5 py-4 text-right text-sm text-gray-200">
                  <span class="inline-block px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">
                    ~4 minutes
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div class="inline-block bg-purple-500/10 px-4 py-1 rounded-full text-sm font-medium text-purple-200 mb-4">
          Deployment workflow
        </div>
        <h3 class="text-2xl md:text-3xl font-semibold mb-4">
          From template to production in a
          <span class="text-emerald-300">few clicks</span>
        </h3>
        <p class="text-gray-200/90 mb-8 max-w-md">
          Standardize how you deploy workloads with a repeatable workflow that
          covers image selection, sizing, security and lifecycle management.
        </p>

        <div class="space-y-6">
          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20 border border-purple-400/60 text-purple-200 font-semibold">
              1
            </div>
            <div>
              <div class="font-semibold mb-1">Select an image profile</div>
              <p class="text-sm text-gray-300">
                Filter by operating system, stack and workload to choose the
                most appropriate starting point for your project.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20 border border-purple-400/60 text-purple-200 font-semibold">
              2
            </div>
            <div>
              <div class="font-semibold mb-1">Apply environment standards</div>
              <p class="text-sm text-gray-300">
                Align networking, storage, backup and monitoring policies with
                your organization’s governance requirements.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20 border border-purple-400/60 text-purple-200 font-semibold">
              3
            </div>
            <div>
              <div class="font-semibold mb-1">Deploy to target environment</div>
              <p class="text-sm text-gray-300">
                Launch to your preferred region or data center with consistent
                parameters across development, staging and production.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4">
            <div class="flex h-10 w-10 items-center justify-center rounded-full bg-purple-500/20 border border-purple-400/60 text-purple-200 font-semibold">
              4
            </div>
            <div>
              <div class="font-semibold mb-1">Maintain and update safely</div>
              <p class="text-sm text-gray-300">
                Roll out updated images on your schedule with clear visibility
                into change history and rollback options.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- CTA Section -->
<section class="relative bg-gradient-to-br from-purple-600 via-violet-700 to-purple-800 text-white overflow-hidden py-20">
  <div class="absolute inset-0 z-0">
    <div class="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
    <div class="absolute bottom-0 left-0 w-96 h-96 bg-violet-400 rounded-full filter blur-3xl opacity-20 animate-pulse"></div>
  </div>
  
  <div class="container mx-auto px-4 relative z-10">
    <div class="text-center max-w-4xl mx-auto">
      <h2 class="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
        Ready to Deploy with Saved Images?
      </h2>
      <p class="text-xl text-gray-100 mb-10">
        Browse our library of preconfigured server images and deploy your infrastructure in minutes with optimized, secure configurations.
      </p>
      
      <div class="flex flex-col sm:flex-row gap-4 justify-center mb-12">
        <a routerLink="/contact" class="bg-white text-purple-600 font-bold py-4 px-8 rounded-xl text-center hover:bg-gray-100 transition-all duration-300 transform hover:-translate-y-1 shadow-xl hover:shadow-2xl inline-flex items-center justify-center">
          <i class="bx bx-image mr-2 text-xl"></i>
          <span class="text-lg">Browse Images</span>
        </a>
        <a routerLink="/contact" class="bg-transparent border-2 border-slate-700/50 text-white font-bold py-4 px-8 rounded-xl text-center hover:bg-slate-700/20 transition-all duration-300 inline-flex items-center justify-center">
          <i class="bx bx-phone mr-2 text-xl"></i>
          <span class="text-lg">Contact Sales</span>
        </a>
      </div>
      
      <div class="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">100+</div>
          <div class="text-gray-200 text-sm">Images Available</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">5min</div>
          <div class="text-gray-200 text-sm">Deploy Time</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">Weekly</div>
          <div class="text-gray-200 text-sm">Updates</div>
        </div>
        <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300">
          <div class="text-4xl font-bold mb-2">24/7</div>
          <div class="text-gray-200 text-sm">Support</div>
        </div>
      </div>
    </div>
  </div>
</section>
